

type QueryValue = string | number | boolean | null | undefined;

type Query = {
    readonly [key: string]: QueryValue | readonly QueryValue[];
};

type RequestOptions = RequestInit & {
    readonly acceptedStatuses?: readonly number[];
};

export class Error extends globalThis.Error {
    readonly status: number;
    readonly statusText: string;
    readonly url: string;
    readonly responseText?: string;
    constructor(response: Response, responseText?: string) {
        super(`HTTP ${response.status} ${response.statusText} for ${response.url}`);
        Object.setPrototypeOf(this, Error.prototype);
        this.name = `RegVarme.Http.Error`;
        this.status = response.status;
        this.statusText = response.statusText;
        this.url = response.url;
        this.responseText = responseText;
    }
}

const isAccepted = (response: Response, acceptedStatuses?: readonly number[]) => acceptedStatuses 
    ? acceptedStatuses.indexOf(response.status) !== -1 : response.ok;

const appendQueryValue = (params: URLSearchParams, key: string, value: QueryValue) => {
    if (value !== null && value !== undefined) {
        params.append(key, String(value));
    }
};

export const withQuery = (url: string, query: Query) => {
    const separator = url.indexOf(`?`) === -1 ? `?` : `&`;
    const params = new URLSearchParams();
    Object.keys(query).forEach(key => {
        const value = query[key];
        if (Array.isArray(value)) {
            value.forEach(item => appendQueryValue(params, key, item));
        }
        else {
            appendQueryValue(params, key, value as QueryValue);
        }
    });
    const encodedQuery = params.toString();
    return encodedQuery ? `${url}${separator}${encodedQuery}` : url;
};

export const formBody = (data: Query) => {
    const params = new URLSearchParams();
    Object.keys(data).forEach(key => {
        const value = data[key];
        if (Array.isArray(value)) {
            value.forEach(item => appendQueryValue(params, key, item));
        }
        else {
            appendQueryValue(params, key, value as QueryValue);
        }
    });
    return params;
};

export const request = async (url: string, options: RequestOptions = {}) => {
    const { acceptedStatuses, ...fetchOptions } = options;
    const response = await fetch(url, {
        credentials: `same-origin`,
        ...fetchOptions
    });
    if (isAccepted(response, acceptedStatuses)) {
        return response;
    }
    // IIS replaces application-written bodies for 4xx/5xx responses in this application.
    const responseText = response.status < 400 ? await response.text() : undefined;
    throw new Error(response, responseText);
};

export const text = async (url: string, options: RequestOptions = {}) => (await request(url, options)).text();

export const json = async <T>(url: string, options: RequestOptions = {}): Promise<T | null> => {
    const body = await text(url, options);
    return body.trim() === `` ? null : JSON.parse(body) as T;
};
